import { WalletTopUpModalComponent } from '@ridango/widgets';

import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentDataService } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';

import { NotificationFacade, ShoppingCartService } from '@ridango/components';
import { CartTicketRequest } from '@ridango/components/lib/purchase/purchase.model';
import Env, { WalletV2Step } from '@ridango/core/env';

import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AppStartupService } from '@ridango/core';
import { Platform } from '@ionic/angular';

@Component({
    selector: 'gl-wallet-loading-page-v2',
    templateUrl: './wallet-loading-page-v2.component.html',
    styleUrls: ['./wallet-loading-page-v2.component.scss']
})
export class WalletLoadingPageV2Component implements OnInit, OnDestroy {
    selectedProduct: string;
    @ViewChild('walletTopUpModal') walletTopUpModal: WalletTopUpModalComponent;
    selectedAmount: number;
    currencySymbol: string;

    private componentDestroyed$ = new Subject<void>();

    locale = this.translate.currentLang;
    activeStep: string;
    steps: WalletV2Step[];
    physicalBackButtonSubscription$: Subscription;

    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        public appStartupService: AppStartupService,
        private paymentData: PaymentDataService,
        private shoppingCartService: ShoppingCartService,
        private notification: NotificationFacade,
        private platform: Platform
    ) {}

    ngOnInit() {
        this.currencySymbol = this.appStartupService.getCurrencySymbol();
        this.route.data.pipe(takeUntil(this.componentDestroyed$)).subscribe((data) => {
            this.activeStep = data['view'];
        });

        this.steps = this.env.WALLET_V2?.STEPS;

        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, () => {
            if (!this.env.PURCHASE_FLOW?.HIDE_WALLET_LOADING_BACK_BUTTON) {
                this.handleBackClicked();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.physicalBackButtonSubscription$) {
            this.physicalBackButtonSubscription$.unsubscribe();
        }
    }

    handleBackClicked() {
        const currentStepIndex = this.steps.findIndex((step) => step.name === this.activeStep);
        if (currentStepIndex > 0) {
            const previousStep = this.steps[currentStepIndex - 1];
            this.activeStep = previousStep.name;
            this.router.navigate([this.translate.currentLang, 'purchase', previousStep.name]);
        }
    }
    handleNextClicked() {
        const currentStepIndex = this.steps.findIndex((step) => step.name === this.activeStep);
        if (currentStepIndex >= 0 && currentStepIndex < this.steps.length - 1) {
            const nextStep = this.steps[currentStepIndex + 1];

            if (nextStep.name === 'payment') {
                this.postToCart()
                    .then((success) => {
                        if (success) {
                            this.activeStep = nextStep.name;
                            this.router.navigate([this.translate.currentLang, 'purchase', nextStep.name]);
                        } else {
                            this.paymentData.setTicketCreationError(true);
                            console.warn('Ticket creation failed. Not proceeding to payment.');
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to create cart:', error);
                    });
            } else {
                this.activeStep = nextStep.name;
                this.router.navigate([this.translate.currentLang, 'purchase', nextStep.name]);
            }
        }
    }

    postToCart(): Promise<void | boolean> {
        return new Promise((resolve, reject) => {
            const selectedProduct = this.paymentData.getProduct();
            const selectedCard = this.paymentData.getCard();

            const cartId = this.shoppingCartService.getCartId() ?? null;

            if (this.env.WMB) {
                const ticket: CartTicketRequest = {
                    productId: selectedProduct.product_id?.toString() || null,
                    cardId: selectedCard.card_nr,
                    ...(selectedProduct.price && { price: { amount: selectedProduct.price } })
                };

                this.shoppingCartService
                    .addProductToCartV2({ shoppingCartId: cartId, tickets: [ticket] })
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            resolve(true);
                        },
                        error: (error) => {
                            console.error('Error adding product to cart:', error);
                            const errorMessage = error?.error?.message || error?.message || 'CART_LOADING_ERROR';
                            this.notification.error(this.translate.instant(errorMessage));
                            reject(error);
                        }
                    });
            } else {
                const ticket = {
                    product_id: selectedProduct.product_id || null,
                    region_id: this.env.REGION_ID,
                    channel: this.env.CHANNEL || 'web',
                    shopping_cart_id: cartId,
                    sms: 'N',
                    card_id: selectedCard.card_nr,
                    card_type: null,
                    ...(selectedProduct.price && { price: selectedProduct.price.toString() })
                };

                this.shoppingCartService
                    .postMultipleTicketPayloads([ticket], cartId)
                    .pipe(take(1))
                    .subscribe({
                        next: (response) => {
                            const tickets = response?.['tickets'] || [];
                            const hasError = tickets.some((ticket) => !!ticket.ticket_creation_error);

                            if (hasError) {
                                console.error('Ticket creation error detected.');
                                const errorMessage =
                                    tickets.find((t) => t.ticket_creation_error)?.ticket_creation_error ||
                                    this.translate.instant('CART_LOADING_ERROR');
                                this.notification.error(this.translate.instant(errorMessage));
                                resolve(false);
                                return;
                            }

                            const newCartId = response?.['shopping_cart_id'];
                            const deletionTimestamp =
                                response?.['timestamp'] || new Date(new Date().valueOf() + 15 * 60 * 1000);

                            if (!cartId && newCartId) {
                                this.shoppingCartService.setCartId(newCartId, deletionTimestamp);
                            }
                            resolve(true);
                        },
                        error: (error) => {
                            console.error('Error posting payload:', error);
                            reject(error);
                        }
                    });
            }
        });
    }
}
